import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { useLanguage } from '@/shared/hooks/useLanguage';
import { useTheme } from '@/app/contexts/ThemeContext';

interface ChartDataPoint {
  month: string;
  value: number;
}

interface SearchVolumeModalProps {
  isOpen: boolean;
  onClose: () => void;
  keyword: string;
  searchVolume: number | null;
  trendData?: number[];
  color: string;
}

const SearchVolumeModal: React.FC<SearchVolumeModalProps> = ({ isOpen, onClose, keyword, searchVolume, trendData, color }) => {
  const { language, isRTL, t } = useLanguage();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Generate 12 months of data from trend data
  const chartData = useMemo<ChartDataPoint[]>(() => {
    if (!trendData || trendData.length === 0) {
      // Generate sample data if no trend data
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return months.map((month, index) => ({
        month: language === 'fa' ? `${index + 1}م` : month,
        value: searchVolume ? Math.floor(searchVolume * (0.8 + Math.random() * 0.4)) : 0
      }));
    }
    
    // Use trend data if available
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthLabels = language === 'fa' 
      ? ['۱م', '۲م', '۳م', '۴م', '۵م', '۶م', '۷م', '۸م', '۹م', '۱۰م', '۱۱م', '۱۲م']
      : months;
    
    // If trend data is shorter than 12 months, pad it
    const paddedTrend = [...trendData];
    while (paddedTrend.length < 12) {
      paddedTrend.push(paddedTrend[paddedTrend.length - 1] || searchVolume || 0);
    }
    
    return paddedTrend.slice(0, 12).map((value, index) => ({
      month: monthLabels[index],
      value: value || 0
    }));
  }, [trendData, searchVolume, language]);

  if (!isOpen) return null;

  const handleExport = () => {
    const csvContent = [
      ['Month', 'Search Volume'],
      ...chartData.map(item => [item.month, item.value])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${keyword}_search_volume.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const modalBg = isDark ? 'bg-gray-800' : 'bg-white';
  const modalText = isDark ? 'text-white' : 'text-gray-900';
  const borderColor = isDark ? 'border-gray-700' : 'border-gray-200';

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center modal-overlay"
      style={{ 
        zIndex: 99999999,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(2px)',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }}
      onClick={onClose}
    >
      <div 
        className={`${modalBg} ${modalText} rounded-lg shadow-2xl p-6 w-full max-w-3xl mx-4 ${borderColor} border-2`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className={`text-xl font-bold ${modalText}`}>
              {t('table.searchVolume')} Chart
            </h2>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
              {keyword}
            </p>
          </div>
          <button
            onClick={onClose}
            className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'} text-2xl font-bold`}
          >
            ×
          </button>
        </div>

        {/* Chart */}
        <div className="mb-6" style={{ height: '300px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
              <XAxis 
                dataKey="month" 
                stroke={isDark ? '#9ca3af' : '#6b7280'}
                tick={{ fill: isDark ? '#9ca3af' : '#6b7280' }}
              />
              <YAxis 
                orientation={isRTL ? 'right' : 'left'}
                stroke={isDark ? '#9ca3af' : '#6b7280'}
                tick={{ 
                  fill: isDark ? '#9ca3af' : '#6b7280',
                  dx: isRTL ? 50 : -5,
                  textAnchor: isRTL ? 'start' : 'end'
                }}
                width={isRTL ? 60 : 60}
                tickFormatter={(value: number) => {
                  if (value >= 1000000) {
                    const formatted = (value / 1000000).toFixed(1);
                    return formatted.endsWith('.0') ? `${formatted.replace('.0', '')}M` : `${formatted}M`;
                  } else if (value >= 1000) {
                    const formatted = (value / 1000).toFixed(1);
                    return formatted.endsWith('.0') ? `${formatted.replace('.0', '')}K` : `${formatted}K`;
                  }
                  return value.toString();
                }}
              />
              <RechartsTooltip 
                contentStyle={{
                  backgroundColor: isDark ? '#1f2937' : '#ffffff',
                  border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                  color: isDark ? '#ffffff' : '#000000'
                }}
              />
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke={color} 
                strokeWidth={3}
                dot={{ fill: color, r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Footer with Export Button */}
        <div className="flex justify-end">
          <button
            onClick={handleExport}
            className={`px-4 py-2 rounded-lg font-semibold text-sm transition-colors`}
            style={{
              backgroundColor: color,
              color: '#ffffff'
            }}
          >
            {t('common.exportCSV')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SearchVolumeModal;

